import { createGenericStore } from '@/stores/utils/genericStore.js'
import axios from 'axios'

const procedureStoreOptions = {
  state: () => ({
    historyByProcedureId: {},
  }),
  getters: {
    getProcedureHistory(state) {
      return (procedureId) => {
        const normalizedProcedureId = Number(procedureId)

        if (!Number.isFinite(normalizedProcedureId) || normalizedProcedureId <= 0) {
          return []
        }

        return state.historyByProcedureId[normalizedProcedureId] ?? []
      }
    },
  },
  actions: {
    async fetchProceduresForBusinessSystem(businessSystemId, params = {}) {
      return this.fetchItems({
        clear: true,
        not_paginated: true,
        business_system_id: Number(businessSystemId),
        ...params,
      })
    },
    async createProcedure(payload) {
      return this.itemCreate(payload)
    },
    async startEditProcedure(procedureId) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${procedureId}/start-edit`)
        this.itemPatchState(data)

        return data
      } catch (err) {
        this.error = err
        console.error(`Error starting edit for procedure ${procedureId}:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async publishProcedure(procedureId) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${procedureId}/publish`)
        this.itemPatchState(data)

        return data
      } catch (err) {
        this.error = err
        console.error(`Error publishing procedure ${procedureId}:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async importProcedureFromWordDocument(procedureId, file = null, options = {}) {
      const shouldTrackLoading = options.trackLoading !== false

      if (shouldTrackLoading) {
        this.isLoading = true
      }

      this.error = null

      try {
        const formData = new FormData()
        const normalizedUploadId = Number(options.uploadId ?? 0)

        if (file) {
          formData.append('document', file)
        }

        if (Number.isFinite(normalizedUploadId) && normalizedUploadId > 0) {
          formData.append('upload_id', String(normalizedUploadId))
        }

        const { data } = await axios.post(`${this.api}/${procedureId}/import-from-word-document`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        if (data?.procedure && typeof data.procedure === 'object') {
          this.itemPatchState(data.procedure)
        }

        return data
      } catch (err) {
        this.error = err
        console.error(`Error importing Word document for procedure ${procedureId}:`, err)
        throw err
      } finally {
        if (shouldTrackLoading) {
          this.isLoading = false
        }
      }
    },
    async fetchProcedureHistory(procedureId) {
      this.error = null
      const normalizedProcedureId = Number(procedureId)

      if (!Number.isFinite(normalizedProcedureId) || normalizedProcedureId <= 0) {
        return []
      }

      try {
        const { data } = await axios.get(`${this.api}/${normalizedProcedureId}/history`)
        const history = Array.isArray(data) ? data : []

        for (const procedure of history) {
          this.itemPatchState(procedure)
        }

        this.historyByProcedureId[normalizedProcedureId] = history

        return history
      } catch (err) {
        this.error = err
        console.error(`Error loading history for procedure ${normalizedProcedureId}:`, err)
        throw err
      }
    },
  },
}

export const useProcedureStore = createGenericStore(
  'procedure',
  '/api/v1/procedures',
  procedureStoreOptions
)
